import { currentQueuedModalAtom } from '@/atoms/modalQueueAtoms';
import { useQueuedModal } from '@/hooks/useQueuedModal';
import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { ScreenName } from '@heidi/runtime/analytics-vocabulary';
import { ModalId } from '@heidi/schema/ml-scribe';
import {
  SESSION_LIST_SPOTLIGHT_TOTAL_STEPS,
  sessionListAnchorsMountedAtom,
  sessionListExpandAnchorAvailableAtom,
  sessionListSpotlightStepAtom,
} from '@heidi/session/data';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useRef } from 'react';

/**
 * Effect-free actions for the tour, safe to call from every step component
 * (multiple callers share the same atom state). Keep the lifecycle effects out
 * of here — those live in `useSessionListSpotlightTour`, mounted exactly once.
 */
export function useSessionListSpotlightActions() {
  const step = useAtomValue(sessionListSpotlightStepAtom);
  const setStep = useSetAtom(sessionListSpotlightStepAtom);
  const { handleClose } = useQueuedModal(ModalId.NEW_SESSION_UPLOAD_SPOTLIGHT);

  /** Advance to the next step; on the last step this is a no-op (use `close`). */
  const next = useCallback(() => {
    setStep((current) => {
      if (current === null) return current;
      return current < SESSION_LIST_SPOTLIGHT_TOTAL_STEPS
        ? ((current + 1) as typeof current)
        : current;
    });
  }, [setStep]);

  /**
   * Close the tour: clear step state, return the queue slot and mark the modal
   * seen so it never shows again. `handleClose` handles the seen-state update +
   * dequeue.
   */
  const close = useCallback(() => {
    setStep(null);
    handleClose();
  }, [handleClose, setStep]);

  return { step, next, close };
}

/** Delay before starting the tour so the session list can render and the user can orient. */
const START_DELAY_MS = 800;

/**
 * Auto-starts the tour at step 1 once NEW_SESSION_UPLOAD_SPOTLIGHT reaches the
 * front of the modal queue, and fires the one-time "shown" screen view. Mount
 * once in a long-lived layout (GlobalModalsWrapper). Mirrors MENU_COACHMARK_TOUR.
 */
export function useSessionListSpotlightTour() {
  const currentModal = useAtomValue(currentQueuedModalAtom);
  const isQueueActive = currentModal?.id === ModalId.NEW_SESSION_UPLOAD_SPOTLIGHT;

  const step = useAtomValue(sessionListSpotlightStepAtom);
  const setStep = useSetAtom(sessionListSpotlightStepAtom);
  const anchorsMounted = useAtomValue(sessionListAnchorsMountedAtom);
  const expandAnchorAvailable = useAtomValue(sessionListExpandAnchorAvailableAtom);
  const { handleClose } = useQueuedModal(ModalId.NEW_SESSION_UPLOAD_SPOTLIGHT);

  // Once the tour has been shown this session, don't re-trigger even in the brief
  // window between finishing and the seen_modals mutation propagating back.
  const hasShownRef = useRef(false);

  const isTourActive = step !== null;

  // Reset if the queue loses priority to a higher-priority modal, so the tour
  // doesn't appear stuck. Allow it to restart when the queue frees up again.
  useEffect(() => {
    if (!isQueueActive && isTourActive) {
      hasShownRef.current = false;
      setStep(null);
    }
  }, [isQueueActive, isTourActive, setStep]);

  // Close the tour if the active step's anchor unmounts mid-way, so it can't sit
  // invisible while holding the queue slot: the list view goes away (switched to
  // the EHR Appointments tab, or the panel leaves the tree), or the step 4
  // expand control self-unmounts after the user clicks "Expand list"
  // (hasOpenSession → false). handleClose marks it seen + returns the slot.
  useEffect(() => {
    if (!isTourActive) return;
    const activeAnchorGone = !anchorsMounted || (step === 4 && !expandAnchorAvailable);
    if (activeAnchorGone) {
      setStep(null);
      handleClose();
    }
  }, [isTourActive, anchorsMounted, expandAnchorAvailable, step, setStep, handleClose]);

  // Auto-start at step 1 once the queue gives permission.
  useEffect(() => {
    if (hasShownRef.current) return;
    if (!isQueueActive) return;
    if (isTourActive) return;

    const timeoutId = window.setTimeout(() => {
      hasShownRef.current = true;
      setStep(1);
      track({
        event_name: TrackingEventName.ScreenViewed,
        props: { screen_name: ScreenName.SessionListSpotlight },
      });
    }, START_DELAY_MS);

    return () => window.clearTimeout(timeoutId);
  }, [isQueueActive, isTourActive, setStep]);

  return { isQueueActive, isTourActive };
}
